import { useCallback } from 'react';
import PropTypes from 'prop-types';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';
import ChevronDown from '@gef-ui/material-icons/ChevronDown';
import ChevronUp from '@gef-ui/material-icons/ChevronUp';
import CircleCheckSolid from '@gef-ui/material-icons/CircleCheckSolid';
import CircleInfo from '@gef-ui/material-icons/CircleInfo';

import TaskFlag from 'components/atoms/SvgrIcons/TaskFlag';
import { INFORMATION, TASK } from 'features/views/taskOverview/constants';
import { getSubheaderState } from 'features/views/taskOverview/selectors';
import { joinString } from 'utils/string';

export const TaskTypeItemHeader = ({ taskType, handleToggle, visible }) => {
	const subheaderState = useSelector(getSubheaderState);
	const hasDoneTask = taskType.taskCountDone > 0;
	const hasOnlyDoneTasks = taskType.taskCountDone > 0 && taskType.taskCountDone === taskType.taskCount;

	const handleOnClick = useCallback(() => {
		handleToggle();
	}, [handleToggle]);

	return (
		<Stack
			direction="row"
			sx={{
				backgroundColor: 'background.paper',
				cursor: 'pointer',
				p: 2,
			}}
			onClick={handleOnClick}
			spacing={2}
		>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				{taskType?.informative === TASK && (
					// todo - temporary custom icon, gef icon not working properly, will be replaced in future
					<TaskFlag sx={{ color: 'primary.light', fontSize: 17, ml: 1 }} />
				)}
				{taskType?.informative === INFORMATION && <CircleInfo sx={{ color: 'info.light' }} />}
			</Box>
			<Stack
				direction="row"
				sx={{
					width: '100%',
					alignItems: 'center',
					justifyContent: 'space-between',
				}}
			>
				<Typography
					variant="h5"
					color="text.primary"
					sx={{
						fontWeight: 'bold',
					}}
				>
					{taskType?.description}
				</Typography>
				<Stack
					direction="row"
					spacing={4}
					sx={{
						alignItems: 'center',
					}}
				>
					{taskType.informative === TASK && (!hasOnlyDoneTasks || subheaderState) && (
						<Stack direction="row" spacing={6}>
							<Tooltip title={getLabelMessage('sidebar:inTime')}>
								<Badge
									badgeContent={taskType?.taskCountBeforeNotificationActive}
									color="success"
									showZero
								/>
							</Tooltip>
							<Tooltip title={getLabelMessage('sidebar:deadlineIsNear')}>
								<Badge
									badgeContent={taskType?.taskCountAfterNotificationActive}
									color="warning"
									showZero
								/>
							</Tooltip>
							<Tooltip title={getLabelMessage('sidebar:afterDeadline')}>
								<Badge badgeContent={taskType?.taskCountAfterDeadlineActive} color="error" showZero />
							</Tooltip>
						</Stack>
					)}
					{taskType.informative === INFORMATION && (!hasOnlyDoneTasks || subheaderState) && (
						<Stack
							direction="row"
							spacing={3}
							sx={{
								alignItems: 'center',
							}}
						>
							<Tooltip title={getLabelMessage('sidebar:unreadAndSum')}>
								<Badge
									badgeContent={joinString('/', [taskType?.taskCountUnread, taskType?.taskCount])}
									color="primary"
									sx={{
										'& .MuiBadge-badge': {
											backgroundColor: 'grey.400',
										},
									}}
								/>
							</Tooltip>
						</Stack>
					)}
					{(hasDoneTask || subheaderState) && (
						<Tooltip title={getLabelMessage('sidebar:done')}>
							<Stack
								direction="row"
								spacing={0.5}
								sx={{
									alignItems: 'center',
								}}
							>
								<CircleCheckSolid sx={{ color: 'info.main' }} />
								<Typography
									variant="body1"
									sx={{
										fontWeight: 'bold',
									}}
								>
									{taskType.taskCountDone}
								</Typography>
							</Stack>
						</Tooltip>
					)}
					{visible ? (
						<ChevronUp id="toggleIcon" sx={{ color: 'primary.main' }} />
					) : (
						<ChevronDown id="toggleIcon" sx={{ color: 'primary.main' }} />
					)}
				</Stack>
			</Stack>
		</Stack>
	);
};

TaskTypeItemHeader.propTypes = {
	handleToggle: PropTypes.func,
	taskType: PropTypes.object.isRequired,
	visible: PropTypes.bool,
};

export default TaskTypeItemHeader;
